import "./account.scss";

import { useState } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import BlockingModal from "common/blocking_modal";
import ModalHeader from "common/blocking_modal/header";
import ModalSubheader from "common/blocking_modal/subheader";
import ModalGraphic from "common/blocking_modal/graphic";
import ModalActions from "common/blocking_modal/actions";
import Button from "common/core/button";
import destructiveAction from "assets/images/destructive-action.svg";
import GoogleLogo from "assets/images/account/google-logo.svg";
import Env from "config/environment";
import { useScript } from "common/core/hooks/use-script";

import Styles from "./google_signin_button.module.scss";

const GSI_NOT_LOADED = "GSI_NOT_LOADED";

export type AuthCode = { code: string };
type Props = {
  signIn: (authCode: AuthCode) => void;
  /** Is this button used in signup as opposed to login. Defaults to `false` */
  forSignUp?: boolean;
  className?: string;
};

function GoogleSignInButton(props: Props) {
  const [isWarningShown, setIsWarningShown] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const scriptStatus = useScript("https://accounts.google.com/gsi/client", {
    id: "google-identity-services",
    removeOnUnmount: true,
  });

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }
    setDisabled(true);
    if (scriptStatus !== "ready") {
      setIsWarningShown(true);
      setDisabled(false);
      return;
    }
    new Promise<AuthCode>((resolve, reject) => {
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: Env.googleSignInAppHost,
        scope: "profile email",
        // The `ux_mode` can be "popup" or "redirect". "popup" is used for a popup window.
        ux_mode: "popup",
        callback: (response: { code?: string; error?: string }) => {
          if (response.code) {
            resolve({ code: response.code });
          } else {
            reject(response);
          }
        },
      });
      client.requestCode();
    })
      .then(props.signIn)
      .catch((error: { error?: string; message?: string }) => {
        if (
          error.error === "popup_closed_by_user" ||
          error.error === "access_denied" ||
          error.message === GSI_NOT_LOADED
        ) {
          setIsWarningShown(true);
        }
      })
      .finally(() => {
        setDisabled(false);
      });
  };
  return (
    <>
      <button
        type="button"
        className={classnames(Styles.googleBtn, props.className)}
        onClick={handleButtonClick}
        aria-disabled={disabled ? "true" : undefined}
      >
        <img src={GoogleLogo} alt="" />
        <FormattedMessage
          id="959d1d6c-7528-4e70-882e-2fc9642c5023"
          defaultMessage="Sign {forSignUp, select, true{up} other{in}} with Google"
          values={{ forSignUp: Boolean(props.forSignUp) }}
        />
      </button>
      <BlockingModal isOpen={isWarningShown}>
        <ModalGraphic>
          <img alt="" src={destructiveAction} />
        </ModalGraphic>
        <ModalHeader>
          <FormattedMessage
            id="85689bdf-66ac-419e-ad4a-379494faee03"
            defaultMessage="Are you using an ad blocker?"
          />
        </ModalHeader>
        <ModalSubheader>
          <FormattedMessage
            id="df36f0be-09d0-4824-b741-11ee019fda5d"
            defaultMessage="It looks like an ad blocker or your privacy settings are preventing Google sign-in. Please disable it or use your email address to continue."
          />
        </ModalSubheader>
        <ModalActions>
          <Button
            onClick={() => setIsWarningShown(false)}
            buttonColor="action"
            buttonSize="large"
            variant="secondary"
          >
            <FormattedMessage id="fdf43b99-143c-4fad-8957-b9e9d8607a11" defaultMessage="Dismiss" />
          </Button>
        </ModalActions>
      </BlockingModal>
    </>
  );
}

export default GoogleSignInButton;
